import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { today, addDays, prettyDay, isoDay } from "@/lib/perform";
import { ChevronLeft, ChevronRight, Check, Plus, Trash2, Clock, Users, CalendarCheck, Dumbbell, Loader2 } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full";
const hhmm = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const localDay = (iso) => isoDay(new Date(iso));
export const mondayOf = (d) => { const dt = new Date(d + "T12:00:00"); return addDays(d, -((dt.getDay() + 6) % 7)); };

function invalidate(qc) {
  ["club-week", "calendar", "next-class", "club-classes", "classes", "wods"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
}

// Book, cancel or log one class
export function useBooking() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const act = async (c, what) => {
    setBusy(c.id);
    try {
      await api.post(`/api/classes/${c.id}/${what}`);
      toast.success(what === "book" ? `Booked: ${c.title}, ${prettyDay(localDay(c.starts_at || c.at), { weekday: "long" })} ${hhmm(c.starts_at || c.at)}` : "Booking cancelled");
      invalidate(qc);
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(null);
  };
  return { busy, book: (c) => act(c, "book"), cancel: (c) => act(c, "cancel") };
}

export function ClassCard({ c, compact = false }) {
  const navigate = useNavigate();
  const { busy, book, cancel } = useBooking();
  const day = localDay(c.starts_at);
  const left = Math.max(0, (c.capacity || 0) - (c.booked_count || 0));
  const started = c.over || new Date(c.starts_at) < new Date();
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid="tt-class"
      className={`rounded-xl border p-3 ${c.is_booked ? "border-acid/60 bg-acid/5" : "border-[#1E222A] bg-[#0B0D11]"} ${c.over ? "opacity-60" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono2 text-xs text-acid flex items-center gap-1"><Clock className="w-3 h-3" /> {hhmm(c.starts_at)}{!compact && ` · ${c.duration_min} min`}</p>
          <p className="font-semibold text-sm truncate mt-0.5">{c.title}</p>
          <p className="text-xs text-[#8A91A6] truncate">{c.trainer_name}{c.over ? "" : ` · ${left} of ${c.capacity} places left`}</p>
        </div>
        {c.is_booked && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-acid text-black text-[11px] font-semibold px-2 h-6"><Check className="w-3 h-3" /> Booked</span>}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {!c.is_booked && !started && (
          <button onClick={() => book(c)} disabled={busy === c.id || left === 0} data-testid="tt-book"
            className="h-8 px-4 rounded-full bg-acid text-black text-xs font-semibold disabled:opacity-40 inline-flex items-center gap-1">
            {busy === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}{left === 0 ? "Full" : "Book"}
          </button>
        )}
        {c.is_booked && !started && (
          <button onClick={() => cancel(c)} disabled={busy === c.id} data-testid="tt-cancel" className="h-8 px-3 rounded-full border border-[#343A4A] text-xs text-[#B7BECB]">Cancel booking</button>
        )}
        {c.is_booked && day <= today() && (
          <button onClick={() => navigate(`/app/today?day=${day}`)} data-testid="tt-log" className="h-8 px-3 rounded-full border border-acid/60 text-acid text-xs inline-flex items-center gap-1">
            <Dumbbell className="w-3 h-3" /> Log the workout
          </button>
        )}
      </div>
    </motion.div>
  );
}

// The club's classes for one week, Monday to Sunday, with booking
export function WeekTimetable() {
  const [week, setWeek] = useState(mondayOf(today()));
  const { data, isLoading } = useQuery({ queryKey: ["club-week", week], queryFn: async () => (await api.get("/api/club/timetable", { params: { start: week, days: 7 } })).data });
  const byDay = useMemo(() => {
    const m = {};
    (data?.classes || []).forEach((c) => { (m[localDay(c.starts_at)] = m[localDay(c.starts_at)] || []).push(c); });
    return m;
  }, [data]);
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const empty = !isLoading && (data?.classes || []).length === 0;
  return (
    <div className="surface rounded-2xl p-4 md:p-5" data-testid="week-timetable">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setWeek(addDays(week, -7))} disabled={week <= mondayOf(today())} className="p-2 rounded-full border border-[#343A4A] disabled:opacity-30" aria-label="Previous week"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-center">
          <p className="font-heading font-black text-lg">{prettyDay(week, { day: "numeric", month: "short" })} to {prettyDay(addDays(week, 6), { day: "numeric", month: "short" })}</p>
          <p className="mono-label text-acid">{week === mondayOf(today()) ? "this week" : "week ahead"}</p>
        </div>
        <button onClick={() => setWeek(addDays(week, 7))} disabled={week >= addDays(mondayOf(today()), 21)} className="p-2 rounded-full border border-[#343A4A] disabled:opacity-30" aria-label="Next week"><ChevronRight className="w-4 h-4" /></button>
      </div>
      {isLoading && <p className="text-sm text-[#8A91A6]">Loading the timetable…</p>}
      {empty && <p className="text-sm text-[#8A91A6] text-center py-6">{data?.staff ? "No classes yet. Add your weekly timetable below and it fills four weeks ahead." : "Your club hasn't published classes for this week yet."}</p>}
      {!empty && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {days.map((d) => (
            <div key={d} className={`${(byDay[d] || []).length === 0 ? "hidden md:block" : ""}`}>
              <p className={`mono-label mb-2 ${d === today() ? "text-acid" : ""}`}>{prettyDay(d, { weekday: "short" })} {Number(d.slice(8))}{d === today() ? " · today" : ""}</p>
              <div className="space-y-2">
                {(byDay[d] || []).map((c) => <ClassCard key={c.id} c={c} compact />)}
                {(byDay[d] || []).length === 0 && <p className="text-xs text-[#343A4A]">no classes</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// For owners and coaches: the weekly timetable that fills everyone's calendar
export function TimetableEditor() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["club-week", "slots"], queryFn: async () => (await api.get("/api/club/timetable", { params: { days: 1 } })).data });
  const blank = { weekday: 0, time: "18:00", title: "", duration_min: 60, capacity: 16, coach: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const slots = data?.slots || [];
  const add = async () => {
    if (!f.title.trim()) { toast.error("Give the class a name, e.g. CrossFit"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/api/club/timetable", { ...f, weekday: Number(f.weekday), duration_min: Number(f.duration_min), capacity: Number(f.capacity), tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      toast.success(`Added. ${r.classes_created} classes are now on everyone's calendar for the next 4 weeks.`);
      setF({ ...blank, weekday: f.weekday, time: f.time });
      invalidate(qc);
    } catch (err) { toast.error(apiErr(err)); }
    setBusy(false);
  };
  const remove = async (s) => {
    if (!window.confirm(`Stop "${s.title}" on ${DAYS[s.weekday]}s at ${s.time}? Upcoming classes from it are removed, including any bookings.`)) return;
    try { const { data: r } = await api.delete(`/api/club/timetable/${s.id}`); toast(`Removed ${r.classes_removed} upcoming classes`); invalidate(qc); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="surface rounded-2xl p-5 space-y-4" data-testid="timetable-editor">
      <div>
        <p className="font-heading font-bold flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-acid" /> Your weekly timetable</p>
        <p className="text-sm text-[#8A91A6] mt-1">Set each regular class once. OCTON keeps the next four weeks on every athlete's calendar, ready to book, and attaches that day's workout to each class.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <select className={field} value={f.weekday} onChange={(e) => setF({ ...f, weekday: e.target.value })} data-testid="slot-day">
          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
        <input className={field} type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="slot-time" />
        <input className={`${field} col-span-2`} placeholder="Class, e.g. CrossFit" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="slot-title" />
        <label className="text-xs text-[#8A91A6]">minutes<input className={field} type="number" min="10" max="300" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: e.target.value })} /></label>
        <label className="text-xs text-[#8A91A6]">places<input className={field} type="number" min="1" max="200" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} /></label>
        <input className={`${field} col-span-2 md:col-span-3`} placeholder="Coach (optional)" value={f.coach} onChange={(e) => setF({ ...f, coach: e.target.value })} />
        <button onClick={add} disabled={busy} data-testid="slot-add" className="col-span-2 md:col-span-3 h-10 rounded-full bg-acid text-black font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to timetable
        </button>
      </div>
      {slots.length === 0 ? <p className="text-sm text-[#565C6F]">No regular classes yet.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {slots.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-xl border border-[#1E222A] bg-[#0B0D11] p-3" data-testid="slot-row">
              <div className="w-14 text-center shrink-0"><p className="mono-label">{DAYS[s.weekday].slice(0, 3)}</p><p className="font-mono2 text-sm text-acid">{s.time}</p></div>
              <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{s.title}</p><p className="text-xs text-[#8A91A6] truncate">{s.duration_min} min · {s.capacity} places{s.coach ? ` · ${s.coach}` : ""}</p></div>
              <button onClick={() => remove(s)} className="p-2 text-[#8A91A6] hover:text-pulseError" aria-label="Remove from timetable"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Dashboard / WOD card: your next booked class, or a nudge to book one
export function NextClassCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["next-class"], enabled: !!(user?.club_id && user?.club_active), retry: false,
    queryFn: async () => (await api.get("/api/club/timetable", { params: { days: 14 } })).data });
  const upcoming = (data?.classes || []).filter((c) => !c.over);
  const mine = upcoming.find((c) => c.is_booked);
  const next = upcoming.find((c) => !c.is_booked && c.booked_count < c.capacity);
  if (!data) return null;
  if (!mine && !next) return null;
  return (
    <div className="surface rounded-2xl p-4 flex flex-wrap items-center gap-4" data-testid="next-class">
      <span className="w-11 h-11 rounded-xl bg-[#B57BFF]/15 text-[#B57BFF] flex items-center justify-center shrink-0"><CalendarCheck className="w-5 h-5" /></span>
      <div className="flex-1 min-w-[12rem]">
        <p className="mono-label text-[#B57BFF]">{mine ? "your next class" : "next class with places"}</p>
        <p className="font-semibold">{(mine || next).title} · {prettyDay(localDay((mine || next).starts_at), { weekday: "long" })} {hhmm((mine || next).starts_at)}</p>
        <p className="text-xs text-[#8A91A6] flex items-center gap-1"><Users className="w-3 h-3" /> {(mine || next).booked_count} booked of {(mine || next).capacity}</p>
      </div>
      {mine ? <ClassActions c={mine} /> : <ClassActions c={next} />}
      <button onClick={() => navigate("/app/timetable")} className="text-sm text-acid">Full timetable</button>
    </div>
  );
}

function ClassActions({ c }) {
  const { busy, book } = useBooking();
  if (c.is_booked) return <span className="inline-flex items-center gap-1 rounded-full bg-acid text-black text-xs font-semibold px-3 h-8"><Check className="w-3 h-3" /> Booked</span>;
  return <button onClick={() => book(c)} disabled={busy === c.id} className="h-9 px-5 rounded-full bg-acid text-black text-sm font-semibold" data-testid="next-book">Book</button>;
}
